import React, { useState, useEffect, useRef } from 'react'

const steps = [{ label: 'Details' }, { label: 'Info' }, { label: 'Symptoms' }]

export default function EnquireModal ({ show, onClose }) {
  const [step, setStep] = useState(0)
  const [form, setForm] = useState({
    name: '',
    age: '',
    phone: '',
    email: '',
    symptoms: [],
    comments: ''
  })
  const [allSymptoms, setAllSymptoms] = useState([])
  const [symptomSearch, setSymptomSearch] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const [highlightedIdx, setHighlightedIdx] = useState(-1)
  const dropdownRef = useRef(null)
  // Fetch symptoms from API
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_BASE_URL}/symptoms/`)
      .then(res => res.json())
      .then(data => setAllSymptoms(data.filter(s => s.active !== false)))
      .catch(() => setAllSymptoms([]))
  }, [])
  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState(null)
  const [showToast, setShowToast] = useState(false)

  // Validation for each step
  const validate = () => {
    let errs = {}
    if (step === 0) {
      if (!form.name.trim()) errs.name = 'Patient name is required'
      if (!form.age.trim()) errs.age = 'Age is required'
      else if (isNaN(form.age) || form.age <= 0 || form.age > 120)
        errs.age = 'Enter a valid age'
    }
    if (step === 1) {
      if (!form.phone.trim()) errs.phone = 'Phone number is required'
      else if (!/^\d{10,15}$/.test(form.phone.trim()))
        errs.phone = 'Enter a valid phone number'
      if (!form.email.trim()) errs.email = 'Email is required'
      else if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,}$/.test(form.email.trim()))
        errs.email = 'Enter a valid email'
    }
    if (step === 2) {
      if (!form.symptoms.length) errs.symptoms = 'Select at least one symptom'
    }
    return errs
  }
  // Symptoms chips logic
  const handleSymptomSearch = e => {
    setSymptomSearch(e.target.value)
    setShowDropdown(true)
    setHighlightedIdx(0)
  }
  // Keyboard navigation for dropdown
  const filteredSymptoms = allSymptoms.filter(
    s =>
      s.name.toLowerCase().includes(symptomSearch.toLowerCase()) &&
      !form.symptoms.some(sel => sel.name.toLowerCase() === s.name.toLowerCase())
  )

  const handleInputKeyDown = e => {
    if (showDropdown && filteredSymptoms.length > 0) {
      if (e.key === 'ArrowDown') {
        setHighlightedIdx(idx => Math.min(idx + 1, filteredSymptoms.length - 1))
        e.preventDefault()
        return
      } else if (e.key === 'ArrowUp') {
        setHighlightedIdx(idx => Math.max(idx - 1, 0))
        e.preventDefault()
        return
      } else if (e.key === 'Enter') {
        if (highlightedIdx >= 0 && highlightedIdx < filteredSymptoms.length) {
          handleAddSymptom(filteredSymptoms[highlightedIdx])
          e.preventDefault()
          return
        }
      }
    }
    // Add custom symptom if Enter or Space pressed and not empty and not in list
    if ((e.key === 'Enter' || e.key === ' ') && symptomSearch.trim()) {
      const alreadyExists = form.symptoms.some(s => s.name.toLowerCase() === symptomSearch.trim().toLowerCase())
      if (!alreadyExists) {
        setForm({ ...form, symptoms: [...form.symptoms, { name: symptomSearch.trim() }] })
        setErrors({ ...errors, symptoms: undefined })
        setSymptomSearch('')
        setShowDropdown(false)
      }
      e.preventDefault()
    }
  }

  const handleAddSymptom = symptom => {
    if (!form.symptoms.some(s => s.name.toLowerCase() === symptom.name.toLowerCase())) {
      setForm({ ...form, symptoms: [...form.symptoms, symptom] })
      setErrors({ ...errors, symptoms: undefined })
    }
    setSymptomSearch('')
    setShowDropdown(false)
  }

  const handleRemoveSymptom = name => {
    setForm({ ...form, symptoms: form.symptoms.filter(s => s.name !== name) })
  }

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside (e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false)
      }
    }
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showDropdown])

  // Ensure hooks above always run; gate rendering here
  if (!show) return null

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value })
    setErrors({ ...errors, [e.target.name]: undefined })
  }

  const handleNext = e => {
    e.preventDefault()
    const errs = validate()
    if (Object.keys(errs).length) {
      setErrors(errs)
      return
    }
    setStep(step + 1)
  }

  const handleBack = () => setStep(step - 1)

  const handleSubmit = async e => {
    e.preventDefault()
    const errs = validate()
    if (Object.keys(errs).length) {
      setErrors(errs)
      return
    }
    setSubmitting(true)
    setSuccess(null)
    try {
      // Send symptoms as comma-separated string
      const payload = { ...form, symptoms: form.symptoms.map(s => s.name).join(',') }
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/enquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
      if (res.ok) {
        setSuccess(
          'Your enquiry has been submitted successfully, We will contact you shortly.'
        )
        setForm({
          name: '',
          age: '',
          phone: '',
          email: '',
          symptoms: [],
          comments: ''
        })
        setStep(0)
        setShowToast(true)
        setTimeout(() => {
          setShowToast(false)
        }, 3500)
        setTimeout(() => {
          onClose()
        }, 1200)
      } else {
        setSuccess('Something went wrong. Please try again.')
      }
    } catch {
      setSuccess('Something went wrong. Please try again.')
    }
    setSubmitting(false)
  }

  return (
    <>
      {showToast && (
        <div className='enquire-toast'>
          <span>Enquiry submitted! We will get back to you soon.</span>
        </div>
      )}
      <div className='enquire-modal-bg' onClick={onClose} />
      <div className='enquire-modal-outer'>
        <div className='enquire-modal-card' role='dialog' aria-modal='true'>
          <button
            className='enquire-modal-close'
            onClick={onClose}
            aria-label='Close'
          >
            &times;
          </button>
          <div className='text-center mb-3'>
            <h3 className='mb-2 fw-bold text-primary'>Enquire Form</h3>
            <div className='text-muted mb-2' style={{ fontSize: '1.1rem' }}>
              Fill in your details and our team will contact you soon.
            </div>
          </div>
          {/* Stepper */}
          <div className='stepper mb-4'>
            {steps.map((s, idx) => (
              <div
                key={s.label}
                className={`stepper-step${idx === step ? ' active' : ''}${
                  idx < step ? ' done' : ''
                }`}
              >
                <div className='stepper-circle'>{idx + 1}</div>
                <div className='stepper-label-below'>{s.label}</div>
                {idx < steps.length - 1 && <div className='stepper-line'></div>}
              </div>
            ))}
          </div>
          {success && (
            <div
              className={`alert ${
                success.includes('successfully')
                  ? 'alert-success'
                  : 'alert-danger'
              } mb-4`}
            >
              {success}
            </div>
          )}
          <form
            onSubmit={step === steps.length - 1 ? handleSubmit : handleNext}
            noValidate
          >
            {step === 0 && (
              <div className='row g-3'>
                <div className='col-12'>
                  <label className='form-label'>
                    Patient Name <span className='text-danger'>*</span>
                  </label>
                  <input
                    name='name'
                    type='text'
                    className={`form-control ${
                      errors.name ? 'is-invalid' : ''
                    }`}
                    placeholder='Patient Name'
                    value={form.name}
                    onChange={handleChange}
                  />
                  {errors.name && (
                    <div className='invalid-feedback'>{errors.name}</div>
                  )}
                </div>
                <div className='col-12'>
                  <label className='form-label'>
                    Age<span className='text-danger'>*</span>
                  </label>
                  <input
                    name='age'
                    type='number'
                    className={`form-control ${errors.age ? 'is-invalid' : ''}`}
                    placeholder='Age'
                    value={form.age}
                    onChange={handleChange}
                    min='0'
                    max='120'
                  />
                  {errors.age && (
                    <div className='invalid-feedback'>{errors.age}</div>
                  )}
                </div>
              </div>
            )}
            {step === 1 && (
              <div className='row g-3'>
                <div className='col-12'>
                  <label className='form-label'>
                    Phone Number <span className='text-danger'>*</span>
                  </label>
                  <input
                    name='phone'
                    type='tel'
                    className={`form-control ${
                      errors.phone ? 'is-invalid' : ''
                    }`}
                    placeholder='Phone Number'
                    value={form.phone}
                    onChange={handleChange}
                  />
                  {errors.phone && (
                    <div className='invalid-feedback'>{errors.phone}</div>
                  )}
                </div>
                <div className='col-12'>
                  <label className='form-label'>
                    Email <span className='text-danger'>*</span>
                  </label>
                  <input
                    name='email'
                    type='email'
                    className={`form-control ${
                      errors.email ? 'is-invalid' : ''
                    }`}
                    placeholder='Email'
                    value={form.email}
                    onChange={handleChange}
                  />
                  {errors.email && (
                    <div className='invalid-feedback'>{errors.email}</div>
                  )}
                </div>
              </div>
            )}
            {step === 2 && (
              <div className='row g-3'>
                <div className='col-12'>
                  <label className='form-label'>
                    Symptoms <span className='text-danger'>*</span>
                  </label>
                  <div className='chips-input-wrapper'>
                    <div className='d-flex flex-wrap mb-2'>
                      {form.symptoms.map(s => (
                        <span
                          key={s.name}
                          className='badge bg-primary me-2 mb-2'
                        >
                          {s.name}
                          <button
                            type='button'
                            className='btn-close btn-close-white btn-sm ms-2'
                            aria-label='Remove'
                            style={{ fontSize: '0.7em' }}
                            onClick={() => handleRemoveSymptom(s.name)}
                          ></button>
                        </span>
                      ))}
                    </div>
                    <div ref={dropdownRef} style={{ position: 'relative' }}>
                      <input
                        type='text'
                        className={`form-control ${
                          errors.symptoms ? 'is-invalid' : ''
                        }`}
                        placeholder='Search and select symptoms'
                        value={symptomSearch}
                        onChange={handleSymptomSearch}
                        onFocus={() => setShowDropdown(true)}
                        autoComplete='off'
                        onKeyDown={handleInputKeyDown}
                      />
                      {showDropdown && (
                        <ul
                          className='dropdown-menu show w-100'
                          style={{
                            maxHeight: 180,
                            overflowY: 'auto',
                            position: 'absolute',
                            zIndex: 10,
                            left: 0
                          }}
                        >
                          {filteredSymptoms.length > 0 ? (
                            filteredSymptoms.map((s, idx) => (
                              <li key={s._id} style={{ padding: 0, margin: 0 }}>
                                <button
                                  type='button'
                                  className={`dropdown-item${
                                    highlightedIdx === idx ? ' active' : ''
                                  }`}
                                  style={{ textAlign: 'left', width: '100%' }}
                                  onMouseEnter={() => setHighlightedIdx(idx)}
                                  onClick={() => handleAddSymptom(s)}
                                >
                                  {s.name}
                                </button>
                              </li>
                            ))
                          ) : (
                            <li
                              className='dropdown-item text-muted'
                              style={{ textAlign: 'left' }}
                            >
                              No symptoms found
                            </li>
                          )}
                        </ul>
                      )}
                    </div>
                    {errors.symptoms && (
                      <div className='invalid-feedback d-block'>
                        {errors.symptoms}
                      </div>
                    )}
                  </div>
                </div>
                <div className='col-12'>
                  <label className='form-label'>Additional Comments</label>
                  <textarea
                    name='comments'
                    rows='2'
                    className='form-control'
                    placeholder='Any additional information'
                    value={form.comments}
                    onChange={handleChange}
                  ></textarea>
                </div>
              </div>
            )}
            <div className='d-flex justify-content-between align-items-center mt-4'>
              {step > 0 ? (
                <button
                  type='button'
                  className='btn btn-outline-primary'
                  onClick={handleBack}
                >
                  Back
                </button>
              ) : (
                <span />
              )}
              <button
                type='submit'
                className='btn btn-primary px-4'
                disabled={submitting}
              >
                {submitting
                  ? 'Submitting...'
                  : step === steps.length - 1
                  ? 'Submit Enquiry'
                  : 'Next'}
              </button>
            </div>
          </form>
        </div>
      </div>
      <style>
        {`
                .enquire-toast {
                    position: fixed;
                    top: 24px;
                    left: 50%;
                    transform: translateX(-50%);
                    background: #198754;
                    color: #fff;
                    padding: 0.75rem 1.5rem;
                    border-radius: 24px;
                    font-size: 1.08rem;
                    z-index: 2000;
                    box-shadow: 0 4px 24px rgba(0,0,0,0.12);
                    animation: fadeInToast 0.4s, fadeOutToast 0.4s 3s;
                }
                @keyframes fadeInToast {
                    from { opacity: 0; transform: translateX(-50%) translateY(-20px);}
                    to { opacity: 1; transform: translateX(-50%) translateY(0);}
                }
                @keyframes fadeOutToast {
                    from { opacity: 1;}
                    to { opacity: 0;}
                }
                .enquire-modal-bg {
                    min-height: 100vh;
                    width: 100vw;
                    background: rgba(0,0,0,0.5);
                    position: fixed;
                    top: 0; left: 0; z-index: 1050;
                }
                .enquire-modal-outer {
                    position: fixed;
                    top: 0; left: 0; width: 100vw; height: 100vh;
                    z-index: 1060;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    pointer-events: none;
                }
                .enquire-modal-card {
                    background: #fff;
                    border-radius: 18px;
                    box-shadow: 0 8px 32px rgba(0,0,0,0.18);
                    padding: 2.5rem 2rem 2rem 2rem;
                    max-width: 480px;
                    width: 100%;
                    margin: 0 1rem;
                    position: relative;
                    animation: fadeInUp .5s;
                    max-height: 90vh;
                    overflow-y: auto;
                    pointer-events: all;
                    display: flex;
                    flex-direction: column;
                }
                .enquire-modal-close {
                    position: absolute;
                    top: 12px;
                    right: 18px;
                    background: none;
                    border: none;
                    font-size: 2rem;
                    color: #888;
                    cursor: pointer;
                    z-index: 2;
                }
                /* Stepper styles */
                .stepper {
                    display: flex;
                    align-items: flex-end;
                    justify-content: center;
                    margin-bottom: 1.5rem;
                    flex-wrap: wrap;
                    gap: 0.5rem;
                }
                .stepper-step {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    position: relative;
                    min-width: 60px;
                }
                .stepper-circle {
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    background: var(--bs-primary, #0d6efd);
                    color: #fff;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 600;
                    font-size: 1.1rem;
                    z-index: 1;
                    border: 2px solid var(--bs-primary, #0d6efd);
                    transition: background 0.2s, color 0.2s;
                }
                .stepper-step.active .stepper-circle {
                    background: #fff;
                    color: var(--bs-primary, #0d6efd);
                }
                .stepper-step.done .stepper-circle {
                    background: var(--bs-primary, #0d6efd);
                    color: #fff;
                }
                .stepper-label-below {
                    margin-top: 0.4rem;
                    font-size: 0.98rem;
                    color: var(--bs-primary, #0d6efd);
                    font-weight: 500;
                    text-align: center;
                    min-width: 60px;
                    line-height: 1.1;
                }
                .stepper-line {
                    position: absolute;
                    top: 16px;
                    left: 100%;
                    width: 32px;
                    height: 2px;
                    background: var(--bs-primary, #0d6efd);
                    z-index: 0;
                }
                @media (max-width: 575.98px) {
                    .enquire-modal-card {
                        padding: 1.2rem 0.5rem;
                        max-width: 99vw;
                        width: 99vw;
                        margin: 0.5rem;
                    }
                    .stepper-label-below {
                        font-size: 0.88rem;
                        min-width: 48px;
                    }
                    .stepper-step {
                        min-width: 48px;
                    }
                    .stepper-line {
                        width: 18px;
                    }
                }
                @media (min-width: 768px) {
                    .enquire-modal-card {
                        max-width: 650px;
                        width: 95vw;
                    }
                }
                @media (min-width: 1200px) {
                    .enquire-modal-card {
                        max-width: 700px;
                    }
                }
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(40px);}
                    to { opacity: 1; transform: translateY(0);}
                }
                `}
      </style>
    </>
  )
}
